/**
 * /admin — what needs attention right now, across the whole cohort.
 *
 * This screen was written when the app served exactly one student, so "the
 * student" was implicit everywhere and the dashboard could name one person's
 * progress without saying whose. It now serves cohorts: exams are general, the
 * roster is real, and a paper can be sat more than once. So every number here
 * states its scope — one attempt, one student, or everyone — and the way into a
 * person's progress is the roster rather than a guess at who the first attempt
 * belonged to.
 *
 * Four questions, in the order they get asked: what is coming up, how is the
 * cohort doing, how did the last attempts go, and what is the group still weak
 * at.
 */
import { Link } from "react-router-dom";
import { CalendarClock, ChevronRight, TriangleAlert, Users } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useCatalog } from ".";
import { AccuracyBar } from "./components/AccuracyBar";
import { AnalyticsCohortScores } from "./components/AnalyticsCohortScores";
import { fetchAttemptFacts, summariseCohort } from "./components/AnalyticsData";
import {
  EmptyState,
  ErrorNote,
  FixtureBanner,
  Loading,
  PageTitle,
  Stat,
} from "./components/Shell";
import { fetchDashboard, fetchUsers } from "./data/client";
import {
  formatDateTime,
  formatMarks,
  formatPercent,
  formatRelativeDays,
} from "./lib/format";
import { useAsync } from "./lib/useAsync";

export function DashboardPage() {
  const catalog = useCatalog();

  /**
   * Two compositions off the same endpoints: `fetchDashboard` for the lists it
   * already assembles, and the attempt facts for everything that needs fields
   * the dashboard view model does not carry (`attempt_no`, the student id).
   * They are fetched together so the screen has one loading state — and the
   * duplicated `GET /api/admin/attempts` is one more reason the v1.1 analytics
   * endpoint is worth having.
   */
  const state = useAsync(async () => {
    const [dashboard, facts] = await Promise.all([
      fetchDashboard(catalog),
      fetchAttemptFacts(catalog),
    ]);
    return { dashboard, facts, cohort: summariseCohort(facts) };
  }, [catalog]);

  /**
   * The roster is a separate, tolerated call: `fetchUsers` has no fixture
   * fallback by design, so on a deployment where `/admin/users` is not live it
   * throws. That must not take the dashboard down — without it the headline
   * simply omits "of N on the roster" rather than showing an invented total.
   */
  const roster = useAsync(() => fetchUsers({ role: "student", active: true }), []);
  const rosterTotal = roster.data?.total ?? null;

  const weakest = state.data?.dashboard.weak_topics[0] ?? null;
  // Recent attempts come from the dashboard view model, which has the student's
  // name but not their id, and no sitting number. Both are on the facts.
  const factByAttempt = new Map(
    (state.data?.facts ?? []).map((f) => [f.attempt_id, f]),
  );

  return (
    <div className="space-y-4">
      <PageTitle
        title="Dashboard"
        meta="Upcoming exams, how the cohort is doing, and the topics still going wrong."
        actions={
          <>
            <Link to="/admin/students">
              <Button>
                <Users className="size-3.5" />
                Students
              </Button>
            </Link>
            <Link to="/admin/exams/new">
              <Button variant="primary">Schedule an exam</Button>
            </Link>
          </>
        }
      />

      <FixtureBanner />

      {state.loading ? (
        <Loading />
      ) : state.error ? (
        <ErrorNote error={state.error} />
      ) : state.data ? (
        <>
          <Card>
            <div className="grid grid-cols-2 divide-x divide-border sm:grid-cols-4">
              <Stat
                label="Students who have sat something"
                value={state.data.cohort.student_count}
                hint={
                  rosterTotal === null
                    ? "Anyone with at least one attempt on record"
                    : `of ${rosterTotal} active on the roster`
                }
              />
              <Stat
                label="Attempts"
                value={state.data.cohort.attempt_count}
                hint={`${state.data.cohort.graded_count} graded, everyone included`}
              />
              <Stat
                label="Average score"
                value={
                  state.data.cohort.mean_ratio === null
                    ? "—"
                    : formatPercent(state.data.cohort.mean_ratio)
                }
                hint={
                  state.data.cohort.graded_count === 0
                    ? "Nothing graded yet"
                    : `Mean % of the paper across all ${state.data.cohort.graded_count} graded attempts`
                }
              />
              <Stat
                label="Cohort's weakest topic"
                value={
                  weakest === null ? (
                    "—"
                  ) : (
                    <span className="text-sm">{weakest.topic_name}</span>
                  )
                }
                tone={weakest === null ? "default" : "violation"}
                hint={
                  weakest === null
                    ? "Nothing graded yet"
                    : `${formatPercent(weakest.accuracy)} over ${weakest.answered} answers, all students`
                }
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Cohort"
              description="Every graded sitting, one row per student, weakest average first. Pick a name for that student's own progress."
              actions={
                <Link to="/admin/students">
                  <Button size="sm">All students</Button>
                </Link>
              }
            />
            <AnalyticsCohortScores rows={state.data.cohort.students} />
          </Card>

          <Card>
            <CardHeader
              title="Upcoming"
              description="Scheduled and draft exams, soonest first. Every exam is open to the whole roster."
              actions={
                <Link to="/admin/exams">
                  <Button size="sm">All exams</Button>
                </Link>
              }
            />
            <Table>
              <THead>
                <tr>
                  <TH>Exam</TH>
                  <TH>Subject</TH>
                  <TH>Scheduled</TH>
                  <TH align="right">Duration</TH>
                  <TH align="right">Questions</TH>
                  <TH align="right">Marks</TH>
                  <TH>Status</TH>
                </tr>
              </THead>
              <TBody>
                {state.data.dashboard.upcoming.length === 0 ? (
                  <TableEmpty colSpan={7}>Nothing scheduled.</TableEmpty>
                ) : (
                  state.data.dashboard.upcoming.map((exam) => (
                    <TR key={exam.exam_id}>
                      <TD>
                        <Link
                          to={`/admin/exams/${exam.exam_id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {exam.title}
                        </Link>
                      </TD>
                      <TD className="text-ink-muted">{exam.subject_name}</TD>
                      <TD>
                        <span className="flex items-center gap-1.5">
                          <CalendarClock className="size-3.5 text-ink-faint" />
                          {exam.timing_mode === "from_start" ? (
                            <span className="text-ink-muted">
                              Anytime
                              <span className="ml-1.5 text-ink-faint">
                                timer starts on begin
                              </span>
                            </span>
                          ) : (
                            <>
                              <time>{formatDateTime(exam.scheduled_at)}</time>
                              <span className="text-ink-faint">
                                · {formatRelativeDays(exam.scheduled_at)}
                              </span>
                            </>
                          )}
                        </span>
                      </TD>
                      <TD align="right" className="tabular">
                        {exam.duration_min}m
                      </TD>
                      <TD align="right" className="tabular">
                        {exam.question_count}
                      </TD>
                      <TD align="right" className="tabular">
                        {formatMarks(exam.total_marks)}
                      </TD>
                      <TD>
                        <Badge tone={exam.status === "scheduled" ? "accent" : "neutral"}>
                          {exam.status}
                        </Badge>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </Card>

          <Card>
            <CardHeader
              title="Recent attempts"
              description="Every student, newest first. Violations are that one attempt's flags — click through for the recording, timeline and per-question breakdown."
              actions={
                <Link to="/admin/attempts">
                  <Button size="sm">All attempts</Button>
                </Link>
              }
            />
            <Table>
              <THead>
                <tr>
                  <TH>Exam</TH>
                  <TH>Student</TH>
                  <TH>Submitted</TH>
                  <TH align="right">Score</TH>
                  <TH align="right">Violations</TH>
                  <TH>Status</TH>
                  <TH />
                </tr>
              </THead>
              <TBody>
                {state.data.dashboard.recent.length === 0 ? (
                  <TableEmpty colSpan={7}>No attempts yet.</TableEmpty>
                ) : (
                  state.data.dashboard.recent.map((attempt) => {
                    const ratio =
                      attempt.max_score && attempt.max_score > 0
                        ? (attempt.score ?? 0) / attempt.max_score
                        : null;
                    const fact = factByAttempt.get(attempt.attempt_id);
                    return (
                      <TR key={attempt.attempt_id}>
                        <TD>
                          <Link
                            to={`/admin/attempts/${attempt.attempt_id}`}
                            className="font-medium text-ink hover:underline"
                          >
                            {attempt.exam_title}
                          </Link>
                          {fact && fact.attempt_no > 1 ? (
                            <span className="ml-1.5 text-2xs text-ink-faint">
                              resit #{fact.attempt_no}
                            </span>
                          ) : null}
                        </TD>
                        <TD>
                          {fact ? (
                            <Link
                              to={`/admin/students/${fact.student_id}`}
                              className="text-ink-muted hover:underline"
                            >
                              {attempt.student_name}
                            </Link>
                          ) : (
                            <span className="text-ink-muted">
                              {attempt.student_name}
                            </span>
                          )}
                        </TD>
                        <TD className="text-ink-muted">
                          <time>{formatDateTime(attempt.submitted_at)}</time>
                        </TD>
                        <TD align="right" className="tabular">
                          {formatMarks(attempt.score)} / {formatMarks(attempt.max_score)}
                          {ratio !== null ? (
                            <span className="ml-1.5 text-ink-faint">
                              {formatPercent(ratio)}
                            </span>
                          ) : null}
                        </TD>
                        <TD align="right">
                          {attempt.total_violations > 0 ? (
                            <Badge tone="violation">
                              <TriangleAlert className="size-3" />
                              {attempt.total_violations}
                            </Badge>
                          ) : (
                            <span className="text-ink-faint">0</span>
                          )}
                        </TD>
                        <TD>
                          <Badge
                            tone={
                              attempt.status === "auto_submitted" ? "review" : "neutral"
                            }
                          >
                            {attempt.status === "auto_submitted"
                              ? "auto-submitted"
                              : attempt.status.replaceAll("_", " ")}
                          </Badge>
                        </TD>
                        <TD align="right">
                          <Link
                            to={`/admin/attempts/${attempt.attempt_id}`}
                            className="inline-flex items-center text-ink-faint hover:text-ink"
                            aria-label={`Review ${attempt.exam_title}`}
                          >
                            <ChevronRight className="size-4" />
                          </Link>
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </Card>

          <Card>
            <CardHeader
              title="Weak topics, cohort-wide"
              description={
                state.data.dashboard.aggregated_from === 0
                  ? "Nothing graded yet."
                  : `Lowest accuracy pooled across every student — ${
                      state.data.dashboard.aggregated_from
                    } graded ${
                      state.data.dashboard.aggregated_from === 1 ? "attempt" : "attempts"
                    } in total. Unanswered questions are excluded, and the n column is the number of answers each percentage is drawn from.`
              }
            />
            {state.data.dashboard.weak_topics.length === 0 ? (
              <EmptyState>No graded answers to summarise yet.</EmptyState>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>Topic</TH>
                    <TH>Subject</TH>
                    <TH align="right">Week</TH>
                    <TH align="right">Correct</TH>
                    <TH>Accuracy, all students</TH>
                  </tr>
                </THead>
                <TBody>
                  {state.data.dashboard.weak_topics.map((topic) => (
                    <TR key={topic.topic_id}>
                      <TD className="font-medium">{topic.topic_name}</TD>
                      <TD className="text-ink-muted">{topic.subject_name}</TD>
                      <TD align="right" className="tabular">
                        {topic.week_no ?? "—"}
                      </TD>
                      <TD align="right" className="tabular">
                        {topic.correct} / {topic.answered}
                      </TD>
                      <TD className="w-64">
                        <AccuracyBar
                          accuracy={topic.accuracy}
                          answered={topic.answered}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
